import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { Loader2, Plus, Pin, Lock, ArrowLeft } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import Sidebar from "@/components/Sidebar";
import Header from "@/components/Header";
import LucideIconByName from "@/components/LucideIconByName";
import MentionTextarea from "@/components/MentionTextarea";
import BBCodeToolbar from "@/components/BBCodeToolbar";
import { useAuth } from "@/contexts/AuthContext";

function timeAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "just now";
    if (diff < 3600) return `${Math.floor(diff / 60)} min ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} hours ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)} days ago`;
    return d.toLocaleDateString();
  } catch { return ""; }
}

export default function ForumPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [composeOpen, setComposeOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);
  const composeBodyRef = React.useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/forums/${slug}`);
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load forum");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (title.trim().length < 4 || !body.trim()) {
      toast.error("Title (min 4 chars) and body are required"); return;
    }
    setPosting(true);
    try {
      const { data: thread } = await api.post(`/forums/${slug}/threads`, { title: title.trim(), body: body.trim() });
      toast.success("Thread posted");
      setTitle(""); setBody(""); setComposeOpen(false);
      navigate(`/forums/${slug}/${thread.thread_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Post failed");
    } finally { setPosting(false); }
  };

  return (
    <div className="min-h-screen bg-[var(--tt-bg)] text-[var(--tt-text)]">
      <div className="flex">
        <Sidebar />
        <div className="flex-1 min-w-0">
          <Header />
          <main className="px-4 md:px-8 py-6 max-w-6xl">
            <Link to="/forums" className="inline-flex items-center gap-1.5 text-xs text-[var(--tt-text-muted)] hover:text-white mb-3">
              <ArrowLeft className="w-3 h-3" /> Forums
            </Link>
            {loading ? (
              <div className="flex items-center gap-2 text-sm text-[var(--tt-text-muted)] py-10">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading...
              </div>
            ) : !data ? (
              <div>Forum not found.</div>
            ) : (
              <>
                {/* Forum header card */}
                <div className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded p-5 mb-4 flex items-start gap-4">
                  <div
                    className="w-14 h-14 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: data.forum.color, boxShadow: `0 2px 12px ${data.forum.color}44` }}
                  >
                    <LucideIconByName name={data.forum.icon} className="w-7 h-7" color="#fff" strokeWidth={2.2} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">{data.forum.name}</h1>
                    {data.forum.description && (
                      <p className="text-sm text-[var(--tt-text-muted)] mt-1">{data.forum.description}</p>
                    )}
                  </div>
                  {user && (
                    <button
                      onClick={() => setComposeOpen((o) => !o)}
                      data-testid="forum-compose-btn"
                      className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded shrink-0"
                    >
                      <Plus className="w-3.5 h-3.5" /> New Thread
                    </button>
                  )}
                </div>

                {composeOpen && (
                  <form
                    onSubmit={submit}
                    data-testid="forum-compose-form"
                    className="bg-[var(--tt-surface)] border border-[var(--tt-primary)]/40 rounded p-4 mb-4"
                  >
                    <input
                      value={title}
                      onChange={(e) => setTitle(e.target.value)}
                      placeholder="Thread title..."
                      maxLength={140}
                      data-testid="forum-compose-title"
                      className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] mb-3"
                    />
                    <BBCodeToolbar textareaRef={composeBodyRef} value={body} onChange={(e) => setBody(e.target.value)} />
                    <MentionTextarea
                      ref={composeBodyRef}
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      placeholder="Share your thoughts... [b]bold[/b], [url=https://...]link[/url], or @mention a user"
                      rows={6}
                      maxLength={8000}
                      data-testid="forum-compose-body"
                      className="w-full bg-[var(--tt-surface-2)] border border-[var(--tt-border)] rounded px-3 py-2 text-sm outline-none focus:border-[var(--tt-primary)] resize-y font-mono-tt"
                    />
                    <div className="flex items-center justify-end gap-2 mt-3">
                      <button type="button" onClick={() => { setComposeOpen(false); setTitle(""); setBody(""); }}
                        className="text-xs uppercase tracking-wider font-bold text-[var(--tt-text-muted)] hover:text-white px-3 py-1.5">
                        Cancel
                      </button>
                      <button type="submit" disabled={posting} data-testid="forum-compose-submit"
                        className="inline-flex items-center gap-1.5 bg-[var(--tt-primary)] hover:bg-[var(--tt-primary-strong)] text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded disabled:opacity-50">
                        {posting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                        Post Thread
                      </button>
                    </div>
                  </form>
                )}

                {/* Thread list - row style */}
                {data.threads.length === 0 ? (
                  <div className="text-center text-sm text-[var(--tt-text-muted)] py-16 border border-dashed border-[var(--tt-border)] rounded">
                    No threads yet. Be the first to start a discussion.
                  </div>
                ) : (
                  <div data-testid="thread-list" className="bg-[var(--tt-surface)] border border-[var(--tt-border)] rounded overflow-hidden">
                    <ul>
                      {data.threads.map((t, idx) => (
                        <li key={t.thread_id} className={idx > 0 ? "border-t border-[var(--tt-border)]" : ""}>
                          <Link
                            to={`/forums/${slug}/${t.thread_id}`}
                            data-testid={`thread-card-${t.thread_id}`}
                            className="flex items-start gap-4 px-5 py-4 hover:bg-white/[0.02] transition-colors"
                          >
                            {/* Avatar */}
                            <div className="shrink-0 mt-0.5">
                              {t.user_picture ? (
                                <img src={t.user_picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                              ) : (
                                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[var(--tt-primary)] to-[var(--tt-purple)] flex items-center justify-center text-sm font-bold">
                                  {(t.user_name || "?")[0].toUpperCase()}
                                </div>
                              )}
                            </div>

                            {/* Title + meta */}
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                {t.pinned && <Pin className="w-3.5 h-3.5 text-[var(--tt-yellow)]" />}
                                {t.locked && <Lock className="w-3.5 h-3.5 text-red-400" />}
                                <h3 className="font-display font-bold text-base text-[var(--tt-primary)] hover:underline truncate">{t.title}</h3>
                              </div>
                              <div className="text-xs text-[var(--tt-text-muted)] mt-1">
                                Started by <span className="text-white/85 font-semibold">{t.user_name}</span> · {timeAgo(t.created_at)}
                              </div>
                            </div>

                            {/* Stats column */}
                            <div className="hidden sm:block w-24 text-right shrink-0 text-xs">
                              <div className="font-display text-base font-extrabold">{t.post_count}</div>
                              <div className="text-[10px] uppercase tracking-wider text-[var(--tt-text-muted)]">posts</div>
                              <div className="text-[10px] text-[var(--tt-text-faint)] mt-0.5">{t.view_count} views</div>
                            </div>

                            {/* Last activity column */}
                            <div className="hidden md:block w-32 text-right shrink-0 text-xs text-[var(--tt-text-muted)]">
                              {timeAgo(t.last_post_at)}
                            </div>
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            )}
          </main>
        </div>
      </div>
    </div>
  );
}
